import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { subscriptionAPI } from '../services/api';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [accessLevel, setAccessLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [lastReminderTime, setLastReminderTime] = useState(0);

  const REMINDER_INTERVAL = 30000; // 30 seconds

  const checkAccess = useCallback(async () => {
    if (!currentUser) {
      setAccessLevel(null);
      setLoading(false);
      return;
    }

    // Master users always have full access
    if (currentUser.isMaster || currentUser.role === 'master') {
      setAccessLevel({
        level: 'full',
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canView: true,
        isExpired: false,
        isCancelled: false,
        message: null
      });
      setLoading(false);
      return;
    }

    // Master Panel users always have full access
    if (currentUser.role === 'master_panel') {
      setAccessLevel({
        level: 'full',
        canCreate: true,
        canEdit: true,
        canDelete: true,
        canView: true,
        isExpired: false,
        isCancelled: false,
        message: null
      });
      setLoading(false);
      return;
    }

    // OFFLINE MODE: Use cached subscription data, don't make API calls
    if (currentUser.isOfflineSession || !navigator.onLine) {
      const subscription = currentUser.subscription;
      let isExpired = false;
      let daysRemaining = null;
      
      if (subscription) {
        const expiryDate = subscription.expiryDate ? new Date(subscription.expiryDate) : null;
        if (expiryDate) {
          const now = new Date();
          isExpired = expiryDate < now;
          daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
          if (daysRemaining < 0) daysRemaining = 0;
        }
        
        // Lifetime plans never expire
        if (subscription.plan === 'PLAN_LIFETIME') {
          isExpired = false;
          daysRemaining = null;
        }
        
        // Active subscriptions with valid status
        if (subscription.status === 'active' && !isExpired) {
          setAccessLevel({
            level: 'full',
            canCreate: true,
            canEdit: false, // View-only in offline mode
            canDelete: false,
            canView: true,
            isExpired: false,
            isCancelled: false,
            daysRemaining,
            expiryDate: subscription.expiryDate,
            message: 'Offline mode - View only'
          });
          setLoading(false);
          return;
        }
      }
      
      // Offline with no valid subscription - still allow viewing cached data
      setAccessLevel({
        level: 'readonly',
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canView: true,
        isExpired: isExpired,
        isCancelled: false,
        message: 'Offline mode - View only'
      });
      setLoading(false);
      return;
    }

    try {
      const access = await subscriptionAPI.getAccessLevel();
      setAccessLevel(access);
      
      // If expired and NOT a lifetime plan, show renewal modal
      // Lifetime plans never expire
      const isLifetime = currentUser?.subscription?.plan === 'PLAN_LIFETIME';
      if (access.isExpired && !isLifetime) {
        setShowRenewalModal(true);
      }
    } catch (error) {
      console.error('Failed to check subscription access:', error);
      
      // If network error, try to use cached subscription data
      const subscription = currentUser.subscription;
      if (subscription && subscription.status === 'active') {
        const expiryDate = subscription.expiryDate ? new Date(subscription.expiryDate) : null;
        const now = new Date();
        const isExpired = expiryDate ? expiryDate < now : false;
        
        // Lifetime plans never expire
        const isLifetime = subscription.plan === 'PLAN_LIFETIME';
        
        if (!isExpired || isLifetime) {
          // Valid subscription, allow access
          setAccessLevel({
            level: 'full',
            canCreate: true,
            canEdit: true,
            canDelete: true,
            canView: true,
            isExpired: false,
            isCancelled: false,
            message: null
          });
          setLoading(false);
          return;
        }
      }
      
      // Default to view-only on error with no valid cached subscription
      setAccessLevel({
        level: 'readonly',
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canView: true,
        isExpired: true,
        message: 'Unable to verify subscription. Please refresh.'
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Check access on mount and when user changes
  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  // Set up renewal reminder interval for expired subscriptions
  // Skip for lifetime subscriptions and offline sessions
  useEffect(() => {
    const isLifetime = currentUser?.subscription?.plan === 'PLAN_LIFETIME' || accessLevel?.isLifetime;
    const isOffline = currentUser?.isOfflineSession || !navigator.onLine;
    
    // Don't show renewal modal in offline mode
    if (!accessLevel?.isExpired || isLifetime || isOffline) return;

    const interval = setInterval(() => {
      // Double-check we're still online before showing modal
      if (!navigator.onLine) return;
      
      const now = Date.now();
      if (now - lastReminderTime >= REMINDER_INTERVAL) {
        setShowRenewalModal(true);
        setLastReminderTime(now);
      }
    }, REMINDER_INTERVAL);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLevel?.isExpired, currentUser?.isOfflineSession]);

  // Check if action is allowed
  const canPerformAction = useCallback((action) => {
    if (!accessLevel) return false;
    
    switch (action) {
      case 'create':
        return accessLevel.canCreate;
      case 'edit':
        return accessLevel.canEdit;
      case 'delete':
        return accessLevel.canDelete;
      case 'view':
        return accessLevel.canView;
      default:
        return accessLevel.level === 'full';
    }
  }, [accessLevel]);

  // Block action with message
  const blockAction = useCallback((action) => {
    if (accessLevel?.isExpired) {
      setShowRenewalModal(true);
      return {
        blocked: true,
        message: 'Your subscription has expired. Please renew to continue.'
      };
    }
    return { blocked: false };
  }, [accessLevel]);

  const dismissRenewalModal = () => {
    setShowRenewalModal(false);
    setLastReminderTime(Date.now());
  };

  const value = {
    accessLevel,
    loading,
    isExpired: accessLevel?.isExpired || false,
    isCancelled: accessLevel?.isCancelled || false,
    canCreate: accessLevel?.canCreate || false,
    canEdit: accessLevel?.canEdit || false,
    canDelete: accessLevel?.canDelete || false,
    canView: accessLevel?.canView || true,
    daysRemaining: accessLevel?.daysRemaining,
    expiryDate: accessLevel?.expiryDate,
    message: accessLevel?.message,
    showRenewalModal,
    setShowRenewalModal,
    dismissRenewalModal,
    canPerformAction,
    blockAction,
    refreshAccess: checkAccess
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
